import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Briefcase, Mail, Lock, User, ArrowLeft } from 'lucide-react';

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('jobseeker');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await register(email, password, name, role);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-[#047857] mb-8 hover:opacity-80 transition-opacity" data-testid="back-to-home">
          <ArrowLeft size={18} strokeWidth={1.5} /> Back to home
        </Link>

        <div className="bg-white rounded-3xl border border-[#E5E0D8] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#047857] flex items-center justify-center">
              <Briefcase size={18} className="text-white" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</h1>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="w-full mb-6 bg-[#F4F1E8] rounded-full p-1">
              <TabsTrigger value="login" className="rounded-full flex-1 data-[state=active]:bg-white" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" className="rounded-full flex-1 data-[state=active]:bg-white" data-testid="register-tab">Sign Up</TabsTrigger>
            </TabsList>

            {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-2xl text-sm mb-4" data-testid="auth-error">{error}</div>}

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-sm text-[#1C2A24]/70">Email</Label>
                  <div className="relative mt-1">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/40" strokeWidth={1.5} />
                    <Input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] h-12" placeholder="your@email.com" required />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-[#1C2A24]/70">Password</Label>
                  <div className="relative mt-1">
                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/40" strokeWidth={1.5} />
                    <Input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10 rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] h-12" placeholder="Enter password" required />
                  </div>
                </div>
                <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-medium">
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
                <div className="text-center">
                  <Link to="/forgot-password" className="text-sm text-[#047857] hover:underline" data-testid="forgot-password-link">Forgot your password?</Link>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label className="text-sm text-[#1C2A24]/70">Full Name</Label>
                  <div className="relative mt-1">
                    <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/40" strokeWidth={1.5} />
                    <Input data-testid="register-name" type="text" value={name} onChange={e => setName(e.target.value)} className="pl-10 rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] h-12" placeholder="Your name" required />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-[#1C2A24]/70">Email</Label>
                  <div className="relative mt-1">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/40" strokeWidth={1.5} />
                    <Input data-testid="register-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] h-12" placeholder="your@email.com" required />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-[#1C2A24]/70">Password</Label>
                  <div className="relative mt-1">
                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/40" strokeWidth={1.5} />
                    <Input data-testid="register-password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10 rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] h-12" placeholder="Choose a password" required />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-[#1C2A24]/70">I am a...</Label>
                  <div className="flex gap-3 mt-2">
                    <button type="button" data-testid="role-jobseeker" onClick={() => setRole('jobseeker')}
                      className={`flex-1 py-3 px-4 rounded-2xl border text-sm font-medium transition-all ${role === 'jobseeker' ? 'bg-[#047857] text-white border-[#047857]' : 'bg-[#F4F1E8] border-[#E5E0D8] text-[#1C2A24]'}`}>
                      <span className="block">Job Seeker</span>
                      <span className="block text-[10px] mt-0.5 opacity-70 font-normal">Find remote work</span>
                    </button>
                    <button type="button" data-testid="role-business" onClick={() => setRole('business')}
                      className={`flex-1 py-3 px-4 rounded-2xl border text-sm font-medium transition-all ${role === 'business' ? 'bg-[#D97757] text-white border-[#D97757]' : 'bg-[#F4F1E8] border-[#E5E0D8] text-[#1C2A24]'}`}>
                      <span className="block">Employer / Business</span>
                      <span className="block text-[10px] mt-0.5 opacity-70 font-normal">Post jobs & hire</span>
                    </button>
                  </div>
                </div>
                <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-medium">
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
